<script lang="ts" module>
	export interface SiteLinkProps {
		shouldNotCloseOnClick?: boolean | undefined;
		showItemsOnlyOnMobile?: boolean | undefined;
		dataCy: string;
		href?: string | undefined;
		label: string | undefined;
		items?: SiteNavLink['items'];
		onClick?: (() => void) | undefined;
		handleClose: () => void;
		class?: string;
		children: import('svelte').Snippet;
	}
</script>

<script lang="ts">
	import classNames from 'classnames';
	import { twMerge } from 'tailwind-merge';
	import { ChevronDownIcon } from '../../../../assets/icons/ChevronDownIcon';
	import { ChevronRightIcon } from '../../../../assets/icons/ChevronRightIcon';
	import { Link } from '../../../../components/Link';
	import { Txt } from '../../../../components/Txt';
	import { toKebabCase } from '../../../../utils/toKebabCase';
	import type { SiteNavLink } from '../../types';
	import { MenuItem } from '../MenuItem';

	let {
		shouldNotCloseOnClick = false,
		showItemsOnlyOnMobile = false,
		dataCy,
		href = undefined,
		label,
		items = [],
		onClick = undefined,
		handleClose,
		children,
		...rest
	}: SiteLinkProps = $props();

	// `showItemsOnlyOnMobile` is a static configuration prop; it seeds the initial expanded state which the user then toggles.

	// svelte-ignore state_referenced_locally
	let expanded = $state(showItemsOnlyOnMobile);
</script>

{#if !showItemsOnlyOnMobile}
	<Link
		{href}
		class={twMerge(
			classNames('flex w-full items-center justify-between px-4 py-4 hover:bg-gray-100 sm:px-6', {
				'cursor-pointer': items?.length
			}),
			rest.class
		)}
		data-cy={`${dataCy}-${toKebabCase(label)}-link`}
		onclick={() => {
			if (items?.length) {
				expanded = !expanded;
			} else {
				if (onClick) {
					onClick();
				}

				if (!shouldNotCloseOnClick) {
					handleClose();
				}
			}
		}}
	>
		<Txt variant="body2" class="text-gray-900">
			{@render children?.()}
		</Txt>
		{#if expanded}
			<ChevronDownIcon color="gray-500" class="h-3 w-3" />
		{:else}
			<ChevronRightIcon color="gray-500" class="h-3 w-3" />
		{/if}
	</Link>
{/if}

{#if items?.length}
	<div class={classNames('overflow-hidden', expanded ? 'h-auto' : 'h-0')}>
		{#each items as { title, subtitle, href, src, Icon, classes, className, onClick, newTab, shouldNotCloseOnClick: itemShouldNotCloseOnClick }}
			<MenuItem
				{classes}
				{Icon}
				class={twMerge('px-4 py-4 hover:bg-gray-100 sm:px-6', className)}
				{title}
				{subtitle}
				{href}
				{newTab}
				{src}
				onClick={() => {
					if (!itemShouldNotCloseOnClick) {
						handleClose();
					}

					if (onClick) {
						onClick();
					}
				}}
			/>
		{/each}
	</div>
{/if}
